import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import loginHeroWebp from '../login-hero.webp';
import loginHeroJpg from '../login-hero.jpg';
import { useCompanySettings } from '../hooks/useCompanySettings';
import LoginAuthForm from '../components/LoginAuthForm';

export default function LoginPage() {
  const { companyName, homeTitle, homeSubtitle } = useCompanySettings();
  const location = useLocation();
  const message = location.state?.message;

  const displayTitle = homeTitle || 'Encontre o imóvel dos seus sonhos';
  const displaySubtitle =
    homeSubtitle || 'Busque entre centenas de imóveis disponíveis em nossa plataforma';

  return (
    <>
      <Helmet>
        <title>Login {companyName ? `— ${companyName}` : ''}</title>
        <link rel="preload" as="image" href={loginHeroWebp} type="image/webp" />
      </Helmet>
      <div className="login-page min-h-screen grid grid-cols-1 lg:grid-cols-[3fr_2fr] font-montserrat antialiased">
        {/* Coluna esquerda — print 1: nome no topo esquerdo; título + subtítulo no rodapé esquerdo da imagem */}
        <section className="relative min-h-[42vh] lg:min-h-screen overflow-hidden">
          <picture className="absolute inset-0 block h-full w-full">
            <source srcSet={loginHeroWebp} type="image/webp" />
            <img
              src={loginHeroJpg}
              alt=""
              width={1600}
              height={1067}
              decoding="async"
              fetchPriority="high"
              className="h-full w-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/20" />
          <div className="relative z-10 flex min-h-[42vh] lg:min-h-screen flex-col justify-between p-8 sm:p-10 lg:p-12 lg:pb-14">
            <div className="text-left">
              <p className="text-white font-bold text-sm sm:text-base uppercase tracking-[0.12em] drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                {companyName || 'Nome da imobiliária'}
              </p>
            </div>
            <div className="text-left max-w-lg pr-4">
              <h1 className="text-white text-[1.65rem] sm:text-3xl lg:text-[2.15rem] font-bold leading-[1.15] tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
                {displayTitle}
              </h1>
              <p className="mt-4 text-white/90 text-sm sm:text-[15px] leading-relaxed font-normal drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
                {displaySubtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Coluna direita — fundo #F4F7FE, formulário centralizado como no print 1 */}
        <section className="flex min-h-screen flex-col bg-[#F4F7FE] lg:min-h-0">
          <div className="flex shrink-0 items-center px-8 pt-8 sm:px-10 sm:pt-10 lg:px-12 lg:pt-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Voltar ao início
            </Link>
          </div>

          <div className="flex flex-1 items-center justify-center px-8 pb-14 pt-6 sm:px-10 lg:px-12 xl:px-16 lg:py-12">
            <div className="w-full">
              <header className="mb-10">
                <h2 className="text-[2.125rem] sm:text-[2.5rem] font-bold text-gray-950 tracking-tight leading-none">
                  Bem-vindo.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-gray-500">
                  Acesse sua conta para gerenciar seu portfólio exclusivo de propriedades de alto padrão.
                </p>
              </header>
              <LoginAuthForm message={message} variant="page" />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
